import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Buildings, Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { SectionHeader, EmptyState, StatusBadge } from "./ui";

const STATUSES = ["Active", "Forming", "Inactive", "Needs Attention"];
const EMPTY = { legal_name: "", dba: "", ein: "", entity_type: "", state: "", city: "", zip_code: "", naics_code: "", owners: [], formation_date: "", address: "", phone: "", email: "", website: "", industry: "", registered_agent: "", status: "Active", formation_status: "", notes: "", client_id: "none", is_my_company: false };

export default function Companies() {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const [c, cl] = await Promise.all([api.get("/cc/companies"), api.get("/cc/clients")]);
    setRows(c.data); setClients(cl.data); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (sp.get("new") === "1") { openNew(); sp.delete("new"); setSp(sp, { replace: true }); }
    const eid = sp.get("edit");
    if (eid) { api.get(`/cc/companies/${eid}`).then(({ data }) => openEdit(data.company || data)).catch(() => {}); sp.delete("edit"); setSp(sp, { replace: true }); }
  }, [sp, setSp]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addOwner = () => setForm((f) => ({ ...f, owners: [...(f.owners || []), { name: "", title: "", ownership_pct: "", email: "", phone: "" }] }));
  const updateOwner = (i, k, v) => setForm((f) => { const o = [...(f.owners || [])]; o[i] = { ...o[i], [k]: v }; return { ...f, owners: o }; });
  const removeOwner = (i) => setForm((f) => ({ ...f, owners: (f.owners || []).filter((_, idx) => idx !== i) }));
  const openNew = () => { setEditingId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({ ...EMPTY, ...c, client_id: c.client_id || "none" });
    setOpen(true);
  };
  const save = async () => {
    if (!form.legal_name.trim()) { toast.error("Legal name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, client_id: form.client_id === "none" ? null : form.client_id };
      delete payload.id; delete payload.client_name; delete payload.created_at; delete payload.updated_at;
      if (editingId) { await api.put(`/cc/companies/${editingId}`, payload); toast.success("Company updated"); }
      else { await api.post("/cc/companies", payload); toast.success("Company created"); }
      setOpen(false); setForm(EMPTY); setEditingId(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.legal_name}"? This cannot be undone.`)) return;
    try { await api.delete(`/cc/companies/${c.id}`); setRows((r) => r.filter((x) => x.id !== c.id)); toast.success("Deleted"); }
    catch { toast.error("Failed to delete"); }
  };

  const shown = rows.filter((r) => tab === "all" ? true : r.is_my_company);

  return (
    <div data-testid="companies-page">
      <SectionHeader title="Companies" subtitle="Client businesses and companies you own or manage."
        action={<Button data-testid="new-company-button" onClick={openNew} className="bg-primary text-primary-foreground hover:bg-[#DBC197] gap-1"><Plus size={18} weight="bold" /> New Company</Button>}
      />

      <div className="flex gap-2 mb-4">
        <Button size="sm" variant={tab === "all" ? "default" : "ghost"} onClick={() => setTab("all")} data-testid="tab-all-companies" className={tab === "all" ? "bg-primary text-primary-foreground" : ""}>All</Button>
        <Button size="sm" variant={tab === "mine" ? "default" : "ghost"} onClick={() => setTab("mine")} data-testid="tab-my-companies" className={tab === "mine" ? "bg-primary text-primary-foreground" : ""}>My Companies</Button>
      </div>

      {loading ? <div className="text-muted-foreground">Loading…</div> :
        shown.length === 0 ? <EmptyState icon={Buildings} title="No companies yet" hint="Add a client business or one of your own companies." /> : (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="companies-list">
            {shown.map((c) => (
              <div key={c.id} data-testid={`company-card-${c.id}`} onClick={() => navigate(`/command/companies/${c.id}`)} className="rounded-xl border border-white/10 bg-zinc-900/50 p-5 cursor-pointer hover:border-primary/30 transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{c.legal_name}</div>
                    <div className="text-xs text-muted-foreground truncate">{[c.entity_type, c.state].filter(Boolean).join(" · ") || "—"}</div>
                  </div>
                  <StatusBadge status={c.status} />
                </div>
                <div className="mt-4 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                  <span className="truncate">{c.is_my_company ? <span className="text-primary">My company</span> : (c.client_name ? `Client: ${c.client_name}` : "Unlinked")}</span>
                  <span className="flex items-center gap-1 shrink-0">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); openEdit(c); }} data-testid={`edit-company-${c.id}`}><PencilSimple size={16} /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-300 hover:text-red-200" onClick={(e) => { e.stopPropagation(); remove(c); }} data-testid={`delete-company-${c.id}`}><Trash size={16} /></Button>
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-white/10 max-w-lg max-h-[85vh] overflow-auto">
          <DialogHeader><DialogTitle>{editingId ? "Edit Company" : "New Company"}</DialogTitle><DialogDescription className="text-sm text-muted-foreground">Create or update a client business or one of your own companies.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label>Legal business name *</Label><Input data-testid="company-name-input" value={form.legal_name} onChange={(e) => set("legal_name", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>DBA</Label><Input value={form.dba} onChange={(e) => set("dba", e.target.value)} /></div>
              <div><Label>EIN</Label><Input value={form.ein} onChange={(e) => set("ein", e.target.value)} /></div>
              <div><Label>Entity type</Label><Input placeholder="LLC, S-Corp…" value={form.entity_type} onChange={(e) => set("entity_type", e.target.value)} /></div>
              <div><Label>Formation date</Label><Input type="date" value={form.formation_date || ""} onChange={(e) => set("formation_date", e.target.value)} /></div>
              <div><Label>Industry</Label><Input value={form.industry} onChange={(e) => set("industry", e.target.value)} /></div>
              <div><Label>NAICS code</Label><Input data-testid="company-naics-input" value={form.naics_code} onChange={(e) => set("naics_code", e.target.value)} /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
              <div><Label>Website</Label><Input value={form.website} onChange={(e) => set("website", e.target.value)} /></div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger data-testid="company-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Address</Label><Input value={form.address} onChange={(e) => set("address", e.target.value)} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>City</Label><Input data-testid="company-city-input" value={form.city} onChange={(e) => set("city", e.target.value)} /></div>
              <div><Label>State</Label><Input data-testid="company-state-input" value={form.state} onChange={(e) => set("state", e.target.value)} /></div>
              <div><Label>Zip</Label><Input data-testid="company-zip-input" value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)} /></div>
            </div>
            <div>
              <Label>Linked client</Label>
              <Select value={form.client_id} onValueChange={(v) => set("client_id", v)}>
                <SelectTrigger data-testid="company-client-select"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent><SelectItem value="none">None</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name || c.business_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Registered agent</Label><Input value={form.registered_agent} onChange={(e) => set("registered_agent", e.target.value)} /></div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Additional business owners</Label>
                <Button type="button" size="sm" variant="outline" className="border-white/15 gap-1" onClick={addOwner} data-testid="add-owner-button"><Plus size={14} /> Add owner</Button>
              </div>
              {(form.owners || []).map((o, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-center" data-testid={`owner-row-${i}`}>
                  <Input className="col-span-4" placeholder="Name" value={o.name || ""} onChange={(e) => updateOwner(i, "name", e.target.value)} data-testid={`owner-name-${i}`} />
                  <Input className="col-span-3" placeholder="Title" value={o.title || ""} onChange={(e) => updateOwner(i, "title", e.target.value)} />
                  <Input className="col-span-2" placeholder="%" value={o.ownership_pct || ""} onChange={(e) => updateOwner(i, "ownership_pct", e.target.value)} />
                  <Input className="col-span-2" placeholder="Email" value={o.email || ""} onChange={(e) => updateOwner(i, "email", e.target.value)} />
                  <Button type="button" size="icon" variant="ghost" className="col-span-1 text-red-300 hover:text-red-200" onClick={() => removeOwner(i)} data-testid={`remove-owner-${i}`}><Trash size={14} /></Button>
                </div>
              ))}
            </div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
            <div className="flex items-center justify-between rounded-lg border border-white/10 p-3">
              <div><div className="text-sm font-medium">This is my company</div><div className="text-xs text-muted-foreground">A company you personally own or manage</div></div>
              <Switch checked={form.is_my_company} onCheckedChange={(v) => set("is_my_company", v)} data-testid="company-mine-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-company-button" disabled={saving} onClick={save} className="bg-primary text-primary-foreground hover:bg-[#DBC197]">{saving ? "Saving…" : editingId ? "Save changes" : "Create company"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
